import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  IAddDispenserCalibrationRequest,
  ICloseBowserReconciliationRequest,
  ICloseMonthEndRequest,
  ICreateFuelDispenserRequest,
  ICreateFuelTransferRequest,
  IDrumConfirmUseRequest,
  IDrumReturnRequest,
  IFuelAuthorizedDrawerGroupResponse,
  IFuelBowserDayDispenseResponse,
  IFuelBowserReconciliationResponse,
  IFuelClaimResponse,
  IFuelClaimSummaryResponse,
  IFuelConfigResponse,
  IFuelContractorStatementResponse,
  IFuelDispenserCalibrationResponse,
  IFuelDispenserResponse,
  IFuelDrawerAuditLogResponse,
  IFuelDrumFloatResponse,
  IFuelMonthEndResponse,
  IFuelPricingResponse,
  IFuelReconciliationResponse,
  IFuelTransferResponse,
  ISaveFuelDipRequest,
  IUpdateAuthorizedDrawersRequest,
  IUpdateFuelClaimStatusRequest,
  IUpdateFuelDispenserRequest,
  IUpdateFuelCustodyConfigRequest,
} from 'types/api/fuelCustodyApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IFuelCustodySlice } from 'types/fuelCustodySlice.interface'
import { FuelDrawerClass } from 'types/fuelEnums.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'

const emptyPaginated = { data: [], totalCount: 0, totalPages: 0, isLoading: false }

const initialState: IFuelCustodySlice = {
  isLoading: false,
  config: null,
  pricing: null,
  claims: { ...emptyPaginated },
  claimSummary: [],
  authorizedDrawers: [],
  drawerAuditLogs: { ...emptyPaginated },
  drums: { ...emptyPaginated },
  contractorStatement: null,
  transfers: { ...emptyPaginated },
  reconciliations: { ...emptyPaginated },
  bowserReconciliations: { ...emptyPaginated },
  dispensers: { ...emptyPaginated },
  dispenserCalibrations: { ...emptyPaginated },
  monthEnd: null,
  bowserDayDispenses: [],
  actionLoading: false,
}

export const getFuelCustodyConfig = createAsyncThunk(
  'fuelCustody/getConfig',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelConfigResponse> = await baseUrl.get('fuel/custody/config')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFuelCustodyConfig = createAsyncThunk(
  'fuelCustody/updateConfig',
  async (body: IUpdateFuelCustodyConfigRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelConfigResponse> = await baseUrl.patch(
        'fuel/custody/config',
        body,
      )
      toast.success('Fuel config updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelCustodyPricing = createAsyncThunk(
  'fuelCustody/getPricing',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelPricingResponse> = await baseUrl.get('fuel/custody/pricing')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelClaims = createAsyncThunk(
  'fuelCustody/getClaims',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelClaimResponse>> = await baseUrl.get(
        'fuel/custody/claim',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelClaimSummary = createAsyncThunk(
  'fuelCustody/getClaimSummary',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelClaimSummaryResponse[]> = await baseUrl.get(
        'fuel/custody/claim/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFuelClaimStatus = createAsyncThunk(
  'fuelCustody/updateClaimStatus',
  async ({ id, body }: { id: number; body: IUpdateFuelClaimStatusRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelClaimResponse> = await baseUrl.patch(
        `fuel/custody/claim/${id}/status`,
        body,
      )
      toast.success('Claim status updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAuthorizedDrawers = createAsyncThunk(
  'fuelCustody/getAuthorizedDrawers',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelAuthorizedDrawerGroupResponse[]> = await baseUrl.get(
        'fuel/custody/authorized-drawers',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateAuthorizedDrawers = createAsyncThunk(
  'fuelCustody/updateAuthorizedDrawers',
  async (
    { machineClass, body }: { machineClass: FuelDrawerClass; body: IUpdateAuthorizedDrawersRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IFuelAuthorizedDrawerGroupResponse> = await baseUrl.put(
        `fuel/custody/authorized-drawers/${machineClass}`,
        body,
      )
      toast.success('Authorized drawers updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDrawerAuditLogs = createAsyncThunk(
  'fuelCustody/getDrawerAuditLogs',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelDrawerAuditLogResponse>> = await baseUrl.get(
        'fuel/custody/authorized-drawers/audit-logs',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelDrums = createAsyncThunk(
  'fuelCustody/getDrums',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelDrumFloatResponse>> = await baseUrl.get(
        'fuel/custody/drum',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmDrumUse = createAsyncThunk(
  'fuelCustody/confirmDrumUse',
  async ({ id, body }: { id: number; body: IDrumConfirmUseRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDrumFloatResponse> = await baseUrl.post(
        `fuel/custody/drum/${id}/confirm-use`,
        body,
      )
      toast.success('Drum usage confirmed')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const returnDrumFuel = createAsyncThunk(
  'fuelCustody/returnDrum',
  async ({ id, body }: { id: number; body: IDrumReturnRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDrumFloatResponse> = await baseUrl.post(
        `fuel/custody/drum/${id}/return`,
        body,
      )
      toast.success('Drum return recorded')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getContractorStatement = createAsyncThunk(
  'fuelCustody/getContractorStatement',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelContractorStatementResponse> = await baseUrl.get(
        'fuel/custody/contractor/statement',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFuelTransfer = createAsyncThunk(
  'fuelCustody/createTransfer',
  async (body: ICreateFuelTransferRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelTransferResponse> = await baseUrl.post(
        'fuel/custody/transfer',
        body,
      )
      toast.success('Transfer recorded')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelTransfers = createAsyncThunk(
  'fuelCustody/getTransfers',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelTransferResponse>> = await baseUrl.get(
        'fuel/custody/transfer',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const saveFuelDip = createAsyncThunk(
  'fuelCustody/saveDip',
  async (body: ISaveFuelDipRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelReconciliationResponse> = await baseUrl.post(
        'fuel/custody/dip',
        body,
      )
      toast.success('Dip saved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelReconciliations = createAsyncThunk(
  'fuelCustody/getReconciliations',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelReconciliationResponse>> = await baseUrl.get(
        'fuel/custody/reconciliation',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const signOffReconciliation = createAsyncThunk(
  'fuelCustody/signOffReconciliation',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelReconciliationResponse> = await baseUrl.post(
        `fuel/custody/reconciliation/${id}/sign-off`,
      )
      toast.success('Day signed off')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBowserReconciliations = createAsyncThunk(
  'fuelCustody/getBowserReconciliations',
  async ({ id, params }: { id: number; params: any }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelBowserReconciliationResponse>> =
        await baseUrl.get(`fuel/custody/bowser/${id}/reconciliation`, {
          params: formatParams(params),
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const closeBowserReconciliation = createAsyncThunk(
  'fuelCustody/closeBowserReconciliation',
  async ({ id, body }: { id: number; body: ICloseBowserReconciliationRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelBowserReconciliationResponse> = await baseUrl.post(
        `fuel/custody/bowser/${id}/reconciliation/close`,
        body,
      )
      toast.success('Bowser day closed')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBowserDayDispenses = createAsyncThunk(
  'fuelCustody/getBowserDayDispenses',
  async ({ id, params }: { id: number; params: any }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelBowserDayDispenseResponse[]> = await baseUrl.get(
        `fuel/custody/bowser/${id}/day`,
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFuelDispensers = createAsyncThunk(
  'fuelCustody/getDispensers',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelDispenserResponse>> = await baseUrl.get(
        'fuel/custody/dispenser',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFuelDispenser = createAsyncThunk(
  'fuelCustody/createDispenser',
  async (body: ICreateFuelDispenserRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDispenserResponse> = await baseUrl.post(
        'fuel/custody/dispenser',
        body,
      )
      toast.success('Dispenser added')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFuelDispenser = createAsyncThunk(
  'fuelCustody/updateDispenser',
  async ({ id, body }: { id: number; body: IUpdateFuelDispenserRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDispenserResponse> = await baseUrl.patch(
        `fuel/custody/dispenser/${id}`,
        body,
      )
      toast.success('Dispenser updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDispenserCalibrations = createAsyncThunk(
  'fuelCustody/getDispenserCalibrations',
  async ({ id, params }: { id: number; params: any }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelDispenserCalibrationResponse>> =
        await baseUrl.get(`fuel/custody/dispenser/${id}/calibration`, {
          params: formatParams(params),
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addDispenserCalibration = createAsyncThunk(
  'fuelCustody/addDispenserCalibration',
  async ({ id, body }: { id: number; body: IAddDispenserCalibrationRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelDispenserResponse> = await baseUrl.post(
        `fuel/custody/dispenser/${id}/calibration`,
        body,
      )
      toast.success('Calibration recorded')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const closeMonthEnd = createAsyncThunk(
  'fuelCustody/closeMonthEnd',
  async (body: ICloseMonthEndRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelMonthEndResponse> = await baseUrl.post(
        'fuel/custody/month-end/close',
        body,
      )
      toast.success('Month-end statement created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMonthEnd = createAsyncThunk(
  'fuelCustody/getMonthEnd',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelMonthEndResponse> = await baseUrl.get('fuel/custody/month-end', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const signOffMonthEnd = createAsyncThunk(
  'fuelCustody/signOffMonthEnd',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelMonthEndResponse> = await baseUrl.post(
        `fuel/custody/month-end/${id}/sign-off`,
      )
      toast.success('Month-end signed off')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const fuelCustodySlice = createSlice({
  name: 'fuelCustody',
  initialState,
  reducers: {
    clearFuelCustody: () => initialState,
    clearReconciliations: (state) => {
      state.reconciliations = { ...emptyPaginated }
    },
    clearBowserReconciliations: (state) => {
      state.bowserReconciliations = { ...emptyPaginated }
    },
    clearFuelClaims: (state) => {
      state.claims = { ...emptyPaginated }
    },
    clearFuelDrums: (state) => {
      state.drums = { ...emptyPaginated }
    },
  },
  extraReducers: {
    [getFuelCustodyConfig.fulfilled.type]: (state, { payload }: { payload: IFuelConfigResponse }) => {
      state.config = payload
    },
    [getFuelCustodyConfig.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFuelCustodyConfig.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelConfigResponse },
    ) => {
      state.config = payload
    },
    [updateFuelCustodyConfig.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFuelCustodyPricing.fulfilled.type]: (state, { payload }: { payload: IFuelPricingResponse }) => {
      state.pricing = payload
    },
    [getFuelClaims.pending.type]: (state) => {
      state.claims.isLoading = true
    },
    [getFuelClaims.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelClaimResponse> },
    ) => {
      state.claims.isLoading = false
      state.claims.data = payload.data
      state.claims.totalCount = payload.totalCount
      state.claims.totalPages = payload.totalPages
    },
    [getFuelClaims.rejected.type]: (state, { payload }) => {
      state.claims.isLoading = false
      handleErrors(payload)
    },
    [getFuelClaimSummary.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelClaimSummaryResponse[] },
    ) => {
      state.claimSummary = payload
    },
    [getAuthorizedDrawers.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelAuthorizedDrawerGroupResponse[] },
    ) => {
      state.authorizedDrawers = payload
    },
    [getFuelDrums.pending.type]: (state) => {
      state.drums.isLoading = true
    },
    [getFuelDrums.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelDrumFloatResponse> },
    ) => {
      state.drums.isLoading = false
      state.drums.data = payload.data
      state.drums.totalCount = payload.totalCount
      state.drums.totalPages = payload.totalPages
    },
    [getFuelDrums.rejected.type]: (state, { payload }) => {
      state.drums.isLoading = false
      handleErrors(payload)
    },
    [getContractorStatement.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelContractorStatementResponse },
    ) => {
      state.contractorStatement = payload
    },
    [getFuelTransfers.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelTransferResponse> },
    ) => {
      state.transfers.data = payload.data
      state.transfers.totalCount = payload.totalCount
      state.transfers.totalPages = payload.totalPages
    },
    [getFuelReconciliations.pending.type]: (state) => {
      state.reconciliations.isLoading = true
    },
    [getFuelReconciliations.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelReconciliationResponse> },
    ) => {
      state.reconciliations.isLoading = false
      state.reconciliations.data = payload.data
      state.reconciliations.totalCount = payload.totalCount
      state.reconciliations.totalPages = payload.totalPages
    },
    [getFuelReconciliations.rejected.type]: (state, { payload }) => {
      state.reconciliations.isLoading = false
      handleErrors(payload)
    },
    [saveFuelDip.fulfilled.type]: (state, { payload }: { payload: IFuelReconciliationResponse }) => {
      const idx = state.reconciliations.data.findIndex((r) => r.id === payload.id)
      if (idx >= 0) state.reconciliations.data[idx] = payload
      else state.reconciliations.data = [payload, ...state.reconciliations.data]
    },
    [signOffReconciliation.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelReconciliationResponse },
    ) => {
      const idx = state.reconciliations.data.findIndex((r) => r.id === payload.id)
      if (idx >= 0) state.reconciliations.data[idx] = payload
    },
    [getBowserReconciliations.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelBowserReconciliationResponse> },
    ) => {
      state.bowserReconciliations.data = payload.data
      state.bowserReconciliations.totalCount = payload.totalCount
      state.bowserReconciliations.totalPages = payload.totalPages
    },
    [getBowserDayDispenses.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelBowserDayDispenseResponse[] },
    ) => {
      state.bowserDayDispenses = payload
    },
    [getFuelDispensers.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelDispenserResponse> },
    ) => {
      state.dispensers.data = payload.data
      state.dispensers.totalCount = payload.totalCount
      state.dispensers.totalPages = payload.totalPages
    },
    [getMonthEnd.fulfilled.type]: (state, { payload }: { payload: IFuelMonthEndResponse }) => {
      state.monthEnd = payload
    },
    [closeMonthEnd.fulfilled.type]: (state, { payload }: { payload: IFuelMonthEndResponse }) => {
      state.monthEnd = payload
    },
    [signOffMonthEnd.fulfilled.type]: (state, { payload }: { payload: IFuelMonthEndResponse }) => {
      state.monthEnd = payload
    },
    [updateFuelClaimStatus.pending.type]: (state) => {
      state.actionLoading = true
    },
    [updateFuelClaimStatus.fulfilled.type]: (state, { payload }: { payload: IFuelClaimResponse }) => {
      state.actionLoading = false
      const idx = state.claims.data.findIndex((c) => c.id === payload.id)
      if (idx >= 0) state.claims.data[idx] = payload
    },
    [updateFuelClaimStatus.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [createFuelTransfer.pending.type]: (state) => {
      state.actionLoading = true
    },
    [createFuelTransfer.fulfilled.type]: (state) => {
      state.actionLoading = false
    },
    [createFuelTransfer.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [saveFuelDip.pending.type]: (state) => {
      state.actionLoading = true
    },
    [saveFuelDip.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [signOffReconciliation.pending.type]: (state) => {
      state.actionLoading = true
    },
    [signOffReconciliation.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [signOffReconciliation.fulfilled.type]: (state) => {
      state.actionLoading = false
    },
  },
})

export const {
  clearFuelCustody,
  clearReconciliations,
  clearBowserReconciliations,
  clearFuelClaims,
  clearFuelDrums,
} = fuelCustodySlice.actions
export default fuelCustodySlice.reducer
